/**
 * Grouping for the add-players drawer's search results (SE-6948, PC2).
 *
 * While a query is active the drawer shows results across ALL players, split
 * into three ordered groups:
 *
 * 1. ALREADY IN THIS PIPELINE — muted, marked "In list", not addable again.
 * 2. RECENTLY VIEWED — matches the user has recently interacted with.
 * 3. ALL PLAYERS — everything else, with the match count.
 *
 * The split is pure derivation over the rows the search returned — a player is
 * never fetched twice, and a player can appear in exactly ONE group (the
 * groups are checked in the order above, so an in-pipeline player the user
 * recently viewed still reads as "In list", which is the state that matters
 * for the add action).
 * @module features/player-kanban/utils/addPlayersSearchGroups
 */

/** The minimum a search row must expose to be grouped. */
export interface SearchGroupable {
  readonly id: string;
}

/** The three PC2 groups, in render order. */
export interface GroupedSearchResults<T extends SearchGroupable> {
  /** Matches already placed on this pipeline — muted "In list" rows. */
  readonly inPipeline: readonly T[];
  /** Matches the user recently interacted with. */
  readonly recentlyViewed: readonly T[];
  /** Every other match. */
  readonly others: readonly T[];
}

/**
 * Split search results into the PC2 groups.
 * @param players - The search matches, in server order.
 * @param pipelinePlayerIds - Ids of players already on this pipeline.
 * @param recentlyViewedIds - Ids of recently interacted players.
 * @returns The three disjoint groups, each preserving the incoming order.
 */
export const groupSearchResults = <T extends SearchGroupable>(
  players: readonly T[],
  pipelinePlayerIds: ReadonlySet<string>,
  recentlyViewedIds: ReadonlySet<string>
): GroupedSearchResults<T> => ({
  inPipeline: players.filter(player => pipelinePlayerIds.has(player.id)),
  recentlyViewed: players.filter(
    player =>
      !pipelinePlayerIds.has(player.id) && recentlyViewedIds.has(player.id)
  ),
  others: players.filter(
    player =>
      !pipelinePlayerIds.has(player.id) && !recentlyViewedIds.has(player.id)
  ),
});
